import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { getDatePickerLocale } from "../i18n/datePickerLocale";
import { useSiteLanguage } from "../i18n/language";
import "./OrganizerDatePicker.css";

const SingleDayPicker = DayPicker as ComponentType<any>;

type OrganizerDatePickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabledBefore?: string;
  disabledAfter?: string;
};

export function formatDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseDateInputValue(value?: string | null) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const [yearRaw, monthRaw, dayRaw] = raw.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export function parseDateDisplayValue(value?: string | null) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (raw.includes("-")) return parseDateInputValue(raw);

  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length !== 8) return null;

  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export function formatDatePickerDisplay(value?: string | null) {
  const date = parseDateInputValue(value);
  if (!date) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;
}

export function isDateRangeInvalid(from?: string | null, to?: string | null) {
  const fromDate = parseDateInputValue(from);
  const toDate = parseDateInputValue(to);
  if (!fromDate || !toDate) return false;
  return toDate.getTime() < fromDate.getTime();
}

function formatDateDraft(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export default function OrganizerDatePicker({
  label,
  value,
  onChange,
  disabledBefore,
  disabledAfter,
}: OrganizerDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(formatDatePickerDisplay(value));
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const { language } = useSiteLanguage();
  const datePickerLocale = getDatePickerLocale(language);
  const selected = parseDateInputValue(value) || undefined;
  const before = parseDateInputValue(disabledBefore);
  const after = parseDateInputValue(disabledAfter);

  useEffect(() => {
    setDraft(formatDatePickerDisplay(value));
  }, [value]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;

    const margin = 12;
    const gap = 8;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const popoverWidth = popoverRect.width;
    const popoverHeight = popoverRect.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const fitsBelow =
      viewportHeight - triggerRect.bottom >= popoverHeight + gap + margin;

    const maxLeft = Math.max(margin, viewportWidth - popoverWidth - margin);
    const maxTop = Math.max(margin, viewportHeight - popoverHeight - margin);
    const left = Math.min(Math.max(margin, triggerRect.left), maxLeft);
    const preferredTop = fitsBelow
      ? triggerRect.bottom + gap
      : triggerRect.top - popoverHeight - gap;
    const top = Math.min(Math.max(margin, preferredTop), maxTop);

    setPopoverStyle({ left, top });
  }, []);

  const commitDraft = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) {
        setDraft("");
        if (value) onChange("");
        return;
      }

      const parsed = parseDateDisplayValue(trimmed);
      if (!parsed) {
        setDraft(formatDatePickerDisplay(value));
        return;
      }

      const nextValue = formatDateInputValue(parsed);
      setDraft(formatDatePickerDisplay(nextValue));
      if (nextValue !== value) onChange(nextValue);
    },
    [onChange, value],
  );

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const frameId = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(frameId);
  }, [open, selected, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePositionChange = () => updatePosition();
    window.addEventListener("resize", handlePositionChange);
    window.addEventListener("scroll", handlePositionChange, true);
    return () => {
      window.removeEventListener("resize", handlePositionChange);
      window.removeEventListener("scroll", handlePositionChange, true);
    };
  }, [open, updatePosition]);

  const popover = (
    <div
      ref={popoverRef}
      className="organizer-stat-date-popover organizer-stat-date-popover--portal"
      style={popoverStyle}
    >
      <SingleDayPicker
        mode="single"
        locale={datePickerLocale}
        selected={selected}
        disabled={{
          ...(before ? { before } : {}),
          ...(after ? { after } : {}),
        }}
        onSelect={(date) => {
          if (!date) return;
          const nextValue = formatDateInputValue(date);
          setDraft(formatDatePickerDisplay(nextValue));
          onChange(nextValue);
          setOpen(false);
        }}
      />
    </div>
  );

  return (
    <div className="organizer-stat-date-picker" ref={containerRef}>
      <span>{label}</span>
      <div
        ref={triggerRef}
        className="organizer-stat-date-trigger"
        onMouseDown={(event) => {
          if (event.target instanceof HTMLElement && event.target.closest("button")) {
            return;
          }
          event.preventDefault();
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          className="organizer-stat-date-input"
          placeholder="dd/mm/aaaa"
          value={draft}
          onChange={(event) => {
            const nextDraft = formatDateDraft(event.target.value);
            setDraft(nextDraft);
            if (nextDraft.length === 10) {
              const parsed = parseDateDisplayValue(nextDraft);
              if (parsed) onChange(formatDateInputValue(parsed));
            }
          }}
          onBlur={() => commitDraft(draft)}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
            }
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft(draft);
            }
          }}
          aria-label={label}
        />
        <button
          type="button"
          className="organizer-stat-date-button"
          onClick={() => setOpen((current) => !current)}
          aria-label={label}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            calendar_month
          </span>
        </button>
      </div>
      {open && createPortal(popover, document.body)}
    </div>
  );
}
